'use client';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCategoriesSearch } from '@eliadmin/core/src/api/products/product-api';
import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { Button } from '@eliadmin/core/src/components/svetech/ui/button';
import { Icon } from '@eliadmin/core/src/components/svetech/ui/icon';
import {
  FormField,
  FormItem,
  FormControl,
  FormMessage,
  Form,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { MultiSelect } from '@eliadmin/core/src/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { SVT_CORNER, SVT_FIELD } from '@eliadmin/core/src/components/ui/svetech-shape';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useSearchBar } from '@eliadmin/core/src/hooks/search-bar/use-search-bar';
import { useGeoLocation } from '@eliadmin/core/src/hooks/use-geo-location';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useRouter } from 'next-nprogress-bar';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';

type FilterSearchProps = {
  className?: {
    container?: string;
    content?: string;
    inputs_container?: string;
    searchInput?: string;
    categorySelect?: string;
    locationSelect?: string;
    submitButton?: string;
    /** Kept for the core API; the Svetech fields are separate boxes without separators. */
    separator?: string;
  };
};

const distanceOptions = [
  { label: '3km', value: 3000 },
  { label: '5km', value: 5000 },
  { label: '10km', value: 10000 },
  { label: '25km', value: 25000 },
  { label: '50km', value: 50000 },
  { label: '100km', value: 100000 },
];

/**
 * Core advanced search form (keyword, categories with the debounced category search, distance
 * from the visitor's location) in the Svetech field row: separate fields 16 px apart and the
 * primary button; stacked on phones. Logic and query parameters are the core ones.
 */
export const FilterSearch = ({ className }: FilterSearchProps) => {
  const locale = useLocaleStore(s => s.currentLocale);
  const form = useForm({
    defaultValues: {
      inputField: '',
      category: [] as { label: string; value: number }[],
      radius: '',
    },
  });

  const { t } = useTranslations();

  const {
    items: categories,
    setSearchQuery,
    isLoading,
  } = useSearchBar({
    fetchFn: query => fetchCategoriesSearch({ locale, query: { results: 5, search: query } }),
    cacheKey: CacheKey.CATEGORIES,
    queryKeyExtra: [locale],
    mapData: data =>
      data.data.map((category: Category) => {
        return {
          label: category.name,
          value: category.id,
        };
      }),
  });

  const router = useRouter();
  const searchParams = useSearchParams();

  const geoLocation = useGeoLocation();

  const handleSubmit = form.handleSubmit(async data => {
    const params = new URLSearchParams(searchParams.toString());
    const keyword = data.inputField ? `${data.inputField}` : '';
    const categoriesArray = data.category.map(category => category.value);

    if (keyword) params.set('search', keyword);
    else params.delete('search');
    if (categoriesArray.length) params.set('pCategory', categoriesArray.join(','));
    else params.delete('pCategory');
    if (data.radius) {
      const { latitude, longitude } = geoLocation.location?.coords ?? {};
      params.set('geoRadius', `${latitude}, ${longitude}, ${data.radius}`);
    } else params.delete('geoRadius');

    router.push(ROUTES.SEARCH_ADVANCED() + '?' + params.toString());
  });

  return (
    <form
      role="search"
      aria-label={t('text2473')}
      onSubmit={e => {
        e.preventDefault();
        handleSubmit();
      }}
      className={cn('w-full', 'ea-filter-search__container', className?.container)}
    >
      <Form {...form}>
        <div
          className={cn(
            'flex w-full flex-col gap-3 md:flex-row md:items-start md:gap-4',
            'ea-filter-search__content',
            className?.content,
          )}
        >
          <div
            className={cn(
              'grid w-full min-w-0 flex-1 gap-3 md:grid-cols-3 md:gap-4',
              'ea-filter-search__inputs',
              className?.inputs_container,
            )}
          >
            <Input
              aria-label={t('text747')}
              placeholder={t('text747')}
              {...form.register('inputField')}
              className={{ wrapper: cn('ea-filter-search__search-input', className?.searchInput) }}
            />
            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'min-w-0 space-y-0',
                    'ea-filter-search__category-multiselect',
                    className?.categorySelect,
                  )}
                >
                  <FormControl>
                    <MultiSelect
                      aria-label={t('text749')}
                      placeholder={t('text749')}
                      {...field}
                      noOptionsMessage={() => t('text752')}
                      options={categories}
                      handleInputChange={setSearchQuery}
                      // react-select has no Svetech primitive: the field box from the shared
                      // shapes (`className` replaces the core container classes), the menu like
                      // the select content.
                      className={cn(
                        SVT_FIELD,
                        SVT_CORNER,
                        'svt-body flex min-h-[var(--svt-field-h)] w-full items-center focus-within:border-[#2c7a25]',
                        'ea-multi-select-container ea-filter-search__category-multiselect-input',
                      )}
                      classNamesConfig={{
                        control: () =>
                          cn(
                            '!min-h-[calc(var(--svt-field-h)-2px)] !px-[var(--svt-field-px)]',
                            'ea-filter-search__category-multiselect-control',
                          ),
                        placeholder: () => 'text-[#6b7681]',
                        menuList: () =>
                          'mt-2 rounded-[12px] rounded-tr-none border-[#e6e8e2] bg-white',
                        option: () => 'svt-body hover:bg-[#f2f3ef]',
                        multiValue: () =>
                          'svt-spec rounded-[5px] rounded-tr-none bg-[#b2d6aa] text-[#14171a]',
                      }}
                      dropdownIndicator={<Icon name="caret-down" className="size-4" />}
                      isLoading={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={'radius'}
              render={({ field }) => (
                <FormItem className="min-w-0 space-y-0">
                  <Select value={String(field.value)} onValueChange={e => field.onChange(e)}>
                    <SelectTrigger
                      aria-label={t('text748')}
                      className={cn('ea-filter-search__location-select', className?.locationSelect)}
                      isClearable={!!field.value}
                      clearableIconPossition="right"
                      value={String(field.value)}
                      onClear={() => {
                        field.onChange(undefined);
                      }}
                    >
                      <SelectValue placeholder={t('text748')} />
                    </SelectTrigger>
                    <SelectContent>
                      {distanceOptions.map((o, index) => (
                        <SelectItem key={index} value={String(o.value)}>
                          {t(o.label as any)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
          </div>
          <Button
            type="submit"
            className={cn(
              'w-full md:w-auto',
              'ea-filter-search__submit-button',
              className?.submitButton,
            )}
          >
            {t('text746')}
          </Button>
        </div>
      </Form>
    </form>
  );
};
